import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTranslation } from "react-i18next"

import { LoadingIndicator } from "components/general/LoadingIndicator"
import { TablePagination } from "components/paginations/TablePagination"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { AdminTokenService } from "services/token/AdminTokenService"
import { TokenDistribution, TokenPool } from "services/token/type"

import { formatDateTime, formatDecimalString, isDecimalString, isPositiveDecimal, tokenErrorCopy, Translate, useIdempotencyKey } from "../helpers/token.helpers"
import { useTokenGate } from "../helpers/useTokenGate"
import { useTokenIdentity } from "../helpers/useTokenIdentity"

import { ReasonModal } from "./shared/ReasonModal"
import { TokenField } from "./shared/TokenField"
import { TokenUnavailable } from "./shared/TokenUnavailable"

const HISTORY_PAGE_SIZE = 10

type Dialog = { kind: "edit" | "top-up" | "distribute"; pool: TokenPool } | null

export const PoolsTab = ({ tenant }: { tenant: string }) => {
  const { t } = useTranslation("translation", { useSuspense: false }) as { t: Translate }
  const { ticker } = useTokenIdentity(tenant)
  const { darkMode } = useProps()
  const { has } = usePermissions()
  const { gate, loading, run } = useTokenGate()

  // Reading pool state comes with PAGE_TOKEN; funding one, editing it, or
  // paying every position in it does not.
  const canManage = has(PERMISSIONS.TOKEN_POOL_MANAGE)

  const [pools, setPools] = useState<TokenPool[]>([])
  const [dialog, setDialog] = useState<Dialog>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | undefined>()

  const [history, setHistory] = useState<TokenDistribution[]>([])
  const [historyPage, setHistoryPage] = useState(1)
  const [historyPages, setHistoryPages] = useState(1)

  // Edit-dialog draft. Strings, like everything else that carries an amount.
  const [weight, setWeight] = useState("")
  const [minStake, setMinStake] = useState("")
  const [weekly, setWeekly] = useState("")
  const [paused, setPaused] = useState(false)
  const [topUpAmount, setTopUpAmount] = useState("")

  // One key per DIALOG OPENING, deliberately not per submit — see the hook. A
  // double-clicked "Fund pool" must carry the same key or it funds twice, and
  // there is no operation that takes it back out.
  const { key: opKey, reset: resetOpKey } = useIdempotencyKey()

  const load = useCallback(async () => {
    if (!tenant) return
    const data = await run(() => AdminTokenService.getConfig(tenant))
    if (data) setPools(data.pools)
  }, [tenant, run])

  const loadHistory = useCallback(async () => {
    if (!tenant) return
    const res = await AdminTokenService.listDistributions(tenant, undefined, historyPage, HISTORY_PAGE_SIZE)
    if (res.ok && res.data) {
      setHistory(res.data.distributions)
      setHistoryPages(res.data.pagination.pages)
    }
  }, [tenant, historyPage])

  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    loadHistory()
  }, [loadHistory])

  const openDialog = (kind: "edit" | "top-up" | "distribute", pool: TokenPool) => {
    setError(undefined)
    resetOpKey()
    if (kind === "edit") {
      setWeight(String(pool.weight))
      setMinStake(pool.min_stake_amount)
      setWeekly(pool.weekly_allocation)
      setPaused(pool.is_paused)
    }
    if (kind === "top-up") setTopUpAmount("")
    setDialog({ kind, pool })
  }

  const weightInvalid = !/^[1-9]\d*$/.test(weight)
  const minStakeInvalid = !isPositiveDecimal(minStake)
  // Zero IS valid: setting a weekly allocation to zero winds a pool down without
  // pausing stakes or touching anyone's locked principal.
  const weeklyInvalid = !isDecimalString(weekly)
  const topUpInvalid = !isPositiveDecimal(topUpAmount)

  const submit = async (reason: string) => {
    if (!dialog) return
    setSubmitting(true)
    setError(undefined)

    const { kind, pool } = dialog
    let res

    if (kind === "edit") {
      // Only genuinely changed fields go up, mirroring the server's own diffing.
      res = await AdminTokenService.updatePool(tenant, reason, pool.id, {
        ...(Number(weight) !== pool.weight && { weight: Number(weight) }),
        ...(minStake !== pool.min_stake_amount && { min_stake_amount: minStake }),
        ...(weekly !== pool.weekly_allocation && { weekly_allocation: weekly }),
        ...(paused !== pool.is_paused && { is_paused: paused })
      })
    } else if (kind === "top-up") {
      res = await AdminTokenService.topUpPool(tenant, reason, pool.id, topUpAmount, opKey)
    } else {
      res = await AdminTokenService.triggerDistribution(tenant, reason, pool.id)
    }

    setSubmitting(false)

    if (!res.ok) {
      setError(tokenErrorCopy(t, res.code, res.message))
      return
    }

    // `already_distributed` is a SUCCESS: the period guard worked and nobody was
    // paid twice. Reporting it as a failure would send an operator looking for a
    // fault that is actually the safety net doing its job.
    if (kind === "distribute") {
      const data = res.data as { already_distributed?: boolean } | undefined
      toast.success(data?.already_distributed ? t("token.alreadyDistributedToast") : t("token.distributionCompleteToast"))
    } else {
      toast.success(kind === "top-up" ? t("token.poolFundedToast") : t("token.poolUpdatedToast"))
    }

    setDialog(null)
    await load()
    await loadHistory()
  }

  if (loading && pools.length === 0) return <LoadingIndicator text={t("token.poolsLoading")} />
  if (gate !== "ok") return <TokenUnavailable variant={gate} onRetry={load} />

  return (
    <div className="grid gap-8">
      {!canManage && <p className="text-sm text-amber-400">{t("token.poolsReadOnly")}</p>}

      <section className="grid gap-5 lg:grid-cols-3">
        {pools.map((pool) => (
          <div key={pool.id} className={clsx("rounded-lg border p-5", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
            <div className="flex items-start justify-between">
              <div>
                <h4 className="text-lg font-semibold">{t("token.lockDays", { days: pool.lock_days })}</h4>
                <p className={clsx("font-mono text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{pool.id}</p>
              </div>
              <span className={clsx("rounded-full px-3 py-1 text-xs", pool.is_paused ? "bg-amber-500/20 text-amber-400" : "bg-green-500/20 text-green-400")}>
                {pool.is_paused ? t("token.poolPaused") : t("token.poolOpen")}
              </span>
            </div>

            <dl className="mt-4 grid gap-2 text-sm">
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.staked")}</dt>
                <dd className="font-mono">{formatDecimalString(pool.total_staked, 2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.poolBalance")}</dt>
                <dd className="font-mono">{formatDecimalString(pool.current_pool_balance, 2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.weeklyAllocation")}</dt>
                <dd className="font-mono">{formatDecimalString(pool.weekly_allocation, 2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.minStake")}</dt>
                <dd className="font-mono">{formatDecimalString(pool.min_stake_amount, 2)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.weight")}</dt>
                <dd className="font-mono">{pool.weight}</dd>
              </div>
              <div className="flex justify-between">
                <dt className={darkMode ? "text-gray-400" : "text-gray-600"}>{t("token.estimatedApr")}</dt>
                <dd className="font-mono">{formatDecimalString(pool.estimated_apr_percent, 2)}%</dd>
              </div>
            </dl>

            {/* Indicative only — both inputs move, so it is not a promise. */}
            <p className={clsx("mt-2 text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.aprIndicative")}</p>

            {/* Absent rather than disabled for an operator without the
                permission: a row of dead buttons invites a support ticket. */}
            {canManage && (
              <div className="mt-5 flex flex-wrap gap-2">
                <button type="button" onClick={() => openDialog("edit", pool)} className="rounded-lg bg-[rgba(132,0,168,1)] px-4 py-2 text-sm text-white hover:bg-[rgba(169,78,206,1)]">
                  {t("token.edit")}
                </button>
                <button
                  type="button"
                  onClick={() => openDialog("top-up", pool)}
                  className={clsx("rounded-lg px-4 py-2 text-sm transition-all", darkMode ? "bg-[rgba(255,255,255,0.06)] text-white" : "bg-gray-100 text-gray-700")}
                >
                  {t("token.fund")}
                </button>
                <button
                  type="button"
                  onClick={() => openDialog("distribute", pool)}
                  className={clsx("rounded-lg px-4 py-2 text-sm transition-all", darkMode ? "bg-[rgba(255,255,255,0.06)] text-white" : "bg-gray-100 text-gray-700")}
                >
                  {t("token.distributeNow")}
                </button>
              </div>
            )}
          </div>
        ))}
      </section>

      <section className={clsx("rounded-lg border p-6", darkMode ? "border-[rgba(255,255,255,0.1)] bg-[#0C0C1A]" : "border-gray-200 bg-white")}>
        <h3 className="mb-5 text-lg font-semibold">{t("token.distributionHistory")}</h3>

        {history.length === 0 ? (
          <p className={clsx("py-8 text-center text-sm", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.noDistributions")}</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={clsx("text-left", darkMode ? "text-gray-400" : "text-gray-600")}>
                    <th className="pb-3 pr-4 font-medium">{t("token.colPeriod")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colPool")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colDistributed")}</th>
                    <th className="pb-3 pr-4 font-medium">{t("token.colPositions")}</th>
                    <th className="pb-3 font-medium">{t("token.colWhen")}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((d) => (
                    <tr key={d.id} className={clsx("border-t", darkMode ? "border-[rgba(255,255,255,0.06)]" : "border-gray-100")}>
                      <td className="py-3 pr-4 font-mono text-xs">{d.period_key}</td>
                      <td className="py-3 pr-4 font-mono text-xs">{d.pool_id}</td>
                      <td className="py-3 pr-4 font-mono">{formatDecimalString(d.total_distributed, 6)}</td>
                      <td className="py-3 pr-4">{d.active_positions}</td>
                      <td className="whitespace-nowrap py-3">{formatDateTime(d.distributed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {historyPages > 1 && <TablePagination page={historyPage} totalPages={historyPages} onJump={setHistoryPage} />}
          </>
        )}
      </section>

      <ReasonModal
        open={dialog?.kind === "edit"}
        title={t("token.editPoolTitle", { days: dialog?.pool.lock_days })}
        confirmLabel={t("token.save")}
        submitting={submitting}
        error={error}
        canSubmit={!weightInvalid && !minStakeInvalid && !weeklyInvalid}
        onCancel={() => setDialog(null)}
        onConfirm={submit}
        description={t("token.editPoolDesc")}
      >
        <TokenField
          label={t("token.weight")}
          value={weight}
          onChange={setWeight}
          invalid={weightInvalid}
          hint={weightInvalid ? t("token.weightInvalid") : t("token.weightHint")}
        />
        <TokenField
          label={t("token.minStakeLabel")}
          value={minStake}
          onChange={setMinStake}
          invalid={minStakeInvalid}
          suffix={ticker}
          hint={minStakeInvalid ? t("token.mustExceedZero") : undefined}
        />
        <TokenField
          label={t("token.weeklyAllocation")}
          value={weekly}
          onChange={setWeekly}
          invalid={weeklyInvalid}
          suffix={ticker}
          hint={weeklyInvalid ? t("token.weeklyAllocationInvalid") : t("token.weeklyAllocationHint")}
        />
        <label className="flex cursor-pointer items-center gap-3">
          <input type="checkbox" checked={paused} onChange={() => setPaused(!paused)} className="h-5 w-5 accent-[rgba(132,0,168,1)]" />
          <span>
            <span className="block text-sm font-medium">{t("token.pausePool")}</span>
            <span className={clsx("block text-xs", darkMode ? "text-gray-500" : "text-gray-500")}>{t("token.pausePoolHint")}</span>
          </span>
        </label>
      </ReasonModal>

      <ReasonModal
        open={dialog?.kind === "top-up"}
        title={t("token.fundPoolTitle", { days: dialog?.pool.lock_days })}
        confirmLabel={t("token.fundPoolConfirm")}
        submitting={submitting}
        error={error}
        canSubmit={!topUpInvalid}
        onCancel={() => setDialog(null)}
        onConfirm={submit}
        description={t("token.fundPoolDesc", { ticker })}
      >
        <TokenField
          label={t("token.amountLabel")}
          value={topUpAmount}
          onChange={setTopUpAmount}
          invalid={topUpInvalid}
          suffix={ticker}
          hint={topUpInvalid ? t("token.mustExceedZero") : t("token.currentBalance", { value: formatDecimalString(dialog?.pool.current_pool_balance, 2) })}
        />
      </ReasonModal>

      <ReasonModal
        open={dialog?.kind === "distribute"}
        title={t("token.distributeTitle", { days: dialog?.pool.lock_days })}
        confirmLabel={t("token.distributeConfirm")}
        submitting={submitting}
        error={error}
        onCancel={() => setDialog(null)}
        onConfirm={submit}
        description={t("token.distributeDesc")}
      />
    </div>
  )
}
